<!-- The rich list row. Factory-owned. Styles in kit.css.

     ONE ROW, FOUR JOBS, IN THIS ORDER OF WEIGHT: the title (what it is), the
     secondary line (the detail that tells two rows apart), the meta (when, how
     many — trailing, tabular), and a status pill when the record has a state.
     The leading chip is an icon on an accent tint, or initials when the row is
     a person, so a column of rows scans by shape before anyone reads a word.

     Built on `IonItem`, not a hand-rolled div: the press feedback, the focus
     ring, the iOS chevron and the Material ripple are all Ionic's, and they are
     correct on both platforms. Put rows in `<IonList :inset="true">`.

     Pass `button` when the row opens something; that is what draws the chevron
     and makes the row one tap target. A click listener on the component lands
     on the IonItem. -->
<template>
  <IonItem
    class="kit-row"
    :button="button"
    :detail="button"
    :lines="lines"
  >
    <!-- A RANKED row leads with its place, not an icon: the order is the
         information ("three replacements, ranked"). -->
    <span v-if="rank" slot="start" class="kit-chip kit-chip--rank" :class="{ 'kit-chip--top': rank === 1 }">
      <span class="sr-only">Number </span>{{ rank }}
    </span>
    <span
      v-else-if="avatar || icon"
      slot="start"
      class="kit-chip"
      :class="{ 'kit-chip--avatar': avatar && !icon }"
      aria-hidden="true"
    >
      <IonIcon v-if="icon" :icon="icon" />
      <template v-else>{{ initials }}</template>
    </span>
    <IonLabel class="kit-row__label">
      <h3 class="kit-row__title">{{ title || fallback }}</h3>
      <p v-if="secondary" class="kit-row__secondary">{{ secondary }}</p>
      <!-- WHY a ranked row is where it is ("Barista · 14 hrs this week ·
           1.2 km away"). Not clamped: a reason cut mid-word is no reason. -->
      <p v-if="reason" class="kit-row__reason">{{ reason }}</p>
    </IonLabel>
    <div v-if="meta || badge" slot="end" class="kit-row__end">
      <span v-if="meta" class="kit-row__meta">{{ meta }}</span>
      <StatusPill v-if="badge" :label="badge" :tone="badgeTone" />
    </div>
  </IonItem>
</template>

<script setup>
import { computed } from 'vue';
import { IonItem, IonLabel, IonIcon } from '@ionic/vue';
import StatusPill from './StatusPill.vue';

const props = defineProps({
  title: { type: [String, Number], default: '' },
  /** What a row shows when the record has no title yet — never `undefined`. */
  fallback: { type: String, default: 'Untitled' },
  secondary: { type: [String, Number], default: '' },
  /** The reason a ranked row holds its place, as one readable line. */
  reason: { type: String, default: '' },
  meta: { type: [String, Number], default: '' },
  /** An ionicons icon (the imported SVG string). */
  icon: { type: String, default: '' },
  /** Text to make initials from, for a person. Ignored when `icon` is set. */
  avatar: { type: String, default: '' },
  badge: { type: String, default: '' },
  badgeTone: { type: String, default: 'neutral' },
  button: { type: Boolean, default: false },
  /** 1, 2, 3… for a ranked list. Replaces the chip; 1 is drawn as the pick. */
  rank: { type: Number, default: 0 },
  lines: { type: String, default: 'full' },
});

const initials = computed(() => String(props.avatar)
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toUpperCase()).join('') || '·');
</script>
